import type { Funnel } from "../../shared/types.ts";
import { MARK, layoutSankey, ribbon, sankeyFont } from "../ui/index.ts";

const FILE = "headcount-job-search.png";
const W = 1200;
const H = 800;
const PAD = 64;
const SCALE = 2;

/** Token values for when the stylesheet isn't there to read, kept in step with `web/app.css`. */
const FALLBACK: Record<string, string> = {
  "--primary": "#1347d4",
  "--accent": "#ff642f",
  "--cream-ink": "#6b5636",
  "--muted": "#646d80",
  "--tone-red-ink": "#b42318",
  "--tone-accent-ink": "#c2410c",
  "--ink": "#161e30",
  "--ink-soft": "#3a4357",
  "--surface": "#ffffff",
  "--line": "rgba(22, 30, 48, 0.09)",
};

function token(name: string): string {
  const v = typeof document === "undefined" ? "" : getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || FALLBACK[name] || "#000000";
}

/** A hex token at `alpha`. Other colour syntaxes pass through and lean on `globalAlpha` instead. */
function rgba(color: string, alpha: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color);
  if (!m) return color;
  const hex = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join("") : m[1]!;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const day = (d: Date, year: boolean) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}) });

/** "Sep 3 to Oct 24, 2026", or one day when the search started today. */
export function dateRange(since: Date, until = new Date()): string {
  if (since.toDateString() === until.toDateString()) return day(until, true);
  const sameYear = since.getFullYear() === until.getFullYear();
  return `${day(since, !sameYear)} to ${day(until, true)}`;
}

function tracked(ctx: CanvasRenderingContext2D, em: number, size: number) {
  if ("letterSpacing" in ctx) (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${em * size}px`;
}

function logo(ctx: CanvasRenderingContext2D, right: number, baseline: number, size: number) {
  const word = size * 0.9;
  ctx.font = sankeyFont(800, word);
  tracked(ctx, -0.045, word);
  const text = ctx.measureText("headcount").width;
  tracked(ctx, 0, word);
  const mark = size * 1.1;
  const unit = mark / 40;
  const markW = (40 - MARK.inset * 2) * unit;
  const x = right - text - size * 0.3 - markW;
  const top = baseline - word * 0.74 - (mark - word * 0.74) / 2;
  ctx.save();
  ctx.translate(x - MARK.inset * unit, top);
  ctx.scale(unit, unit);
  for (const p of MARK.parts) {
    ctx.fillStyle = token(p.fill.slice(4, -1));
    ctx.fill(new Path2D(p.d));
  }
  ctx.restore();
  ctx.font = sankeyFont(800, word);
  tracked(ctx, -0.045, word);
  ctx.fillStyle = token("--ink");
  ctx.textBaseline = "alphabetic";
  ctx.fillText("headcount", right - text, baseline);
  tracked(ctx, 0, word);
}

/** The chart as a 2400×1600 PNG: a title, the Sankey, the date range and the Headcount logo. */
export async function sankeyPng(funnel: Funnel, opts: { since?: Date | null } = {}): Promise<Blob> {
  await Promise.all([600, 800].map((w) => document.fonts?.load(sankeyFont(w, 20)).catch(() => null)));
  const canvas = document.createElement("canvas");
  canvas.width = W * SCALE;
  canvas.height = H * SCALE;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = token("--surface");
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = token("--ink");
  ctx.textBaseline = "alphabetic";
  ctx.font = sankeyFont(800, 44);
  tracked(ctx, -0.03, 44);
  ctx.fillText("My job search.", PAD, PAD + 36);
  tracked(ctx, 0, 44);

  const sub = opts.since ? dateRange(opts.since) : `${funnel.sent} sent, ${funnel.interviews} ${funnel.interviews === 1 ? "interview" : "interviews"}`;
  ctx.font = sankeyFont(500, 19);
  ctx.fillStyle = token("--muted");
  ctx.fillText(sub, PAD, PAD + 72);
  logo(ctx, W - PAD, PAD + 30, 30);

  const top = PAD + 120;
  const chart = layoutSankey(funnel, { width: W - PAD * 2, height: H - top - PAD, fontSize: 19, nodeWidth: 12, gap: 18 });
  ctx.save();
  ctx.translate(PAD, top);
  for (const l of chart.links) {
    const g = ctx.createLinearGradient(l.x0, 0, l.x1, 0);
    g.addColorStop(0, rgba(token(l.to.color), ribbon(l.to.alpha) * 0.6));
    g.addColorStop(1, rgba(token(l.to.color), ribbon(l.to.alpha)));
    ctx.fillStyle = g;
    ctx.fill(new Path2D(l.d));
  }
  for (const n of chart.nodes) {
    ctx.fillStyle = rgba(token(n.color), n.alpha === 1 ? 1 : Math.min(1, n.alpha + 0.25));
    ctx.fillRect(n.x, n.y, chart.nodeWidth, n.h);
  }
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.lineWidth = 5;
  ctx.strokeStyle = token("--surface");
  for (const n of chart.nodes) {
    const x = n.labelX;
    const name = `${n.label} `;
    ctx.font = sankeyFont(600, chart.fontSize);
    const w = ctx.measureText(name).width;
    if (!n.above) ctx.strokeText(name, x, n.labelY);
    ctx.fillStyle = token("--ink-soft");
    ctx.fillText(name, x, n.labelY);
    ctx.font = sankeyFont(800, chart.fontSize);
    if (!n.above) ctx.strokeText(String(n.value), x + w, n.labelY);
    ctx.fillStyle = token("--ink");
    ctx.fillText(String(n.value), x + w, n.labelY);
  }
  ctx.restore();

  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("png_failed"))), "image/png"));
}

/**
 * Shares the chart as a PNG through the system share sheet on phones and tablets, and downloads it everywhere else.
 * Resolves to what happened, so the caller can say so.
 */
export async function shareSankey(funnel: Funnel, opts: { since?: Date | null } = {}): Promise<"shared" | "downloaded" | "cancelled"> {
  const blob = await sankeyPng(funnel, opts);
  const file = new File([blob], FILE, { type: "image/png" });
  const touch = matchMedia("(pointer: coarse)").matches;
  if (touch && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "My job search" });
      return "shared";
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return "cancelled";
    }
  }
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: FILE });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return "downloaded";
}
